import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { fetchDashboardKPICards } from "../../../API_Service/Dashboard_Slice";
import { companyID, KPICard } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../../Common/useDashboardSignalR";

const KPICards = () => {
  const dispatch = useDispatch();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStats = useCallback(() => {
    if (!companyID) {
      setError("Company ID not found");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    dispatch(fetchDashboardKPICards({ companyID }))
      .then((result) => {
        if (result.payload && result.payload.length > 0) {
          setStats(result.payload[0]);
          setError(null);
        } else if (result.payload && !Array.isArray(result.payload)) {
          setStats(result.payload);
        }
      })
      .catch((err) => {
        setError(err?.message || "Failed to load KPI cards");
      })
      .finally(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useDashboardSignalR(loadStats);

  const cards = stats
    ? [
        {
          icon: "assignment",
          label: "Total Projects",
          value: stats.TotalProjects,
          iconColor: "text-blue-600",
          gradientFrom: "from-blue-500",
          gradientTo: "to-blue-600",
        },
        {
          icon: "trending_up",
          label: "Running Projects",
          value: stats.RunningProjects,
          iconColor: "text-green-600",
          gradientFrom: "from-green-500",
          gradientTo: "to-emerald-600",
        },
        {
          icon: "schedule",
          label: "Upcoming Projects",
          value: stats.UpcomingProjects,
          iconColor: "text-purple-600",
          gradientFrom: "from-purple-500",
          gradientTo: "to-purple-600",
        },
        {
          icon: "stop_circle",
          label: "Completed Projects",
          value: stats.CompletedProjects,
          iconColor: "text-orange-600",
          gradientFrom: "from-orange-500",
          gradientTo: "to-red-600",
        },
        {
          icon: "access_time",
          label: "Billable Hours",
          value: stats.TotalBillableHours,
          iconColor: "text-indigo-600",
          gradientFrom: "from-indigo-500",
          gradientTo: "to-indigo-600",
        },
        {
          icon: "hourglass_empty",
          label: "Non-Billable Hours",
          value: stats.TotalNonBillableHours,
          iconColor: "text-red-600",
          gradientFrom: "from-red-500",
          gradientTo: "to-pink-600",
        },
        {
          icon: "check_circle",
          label: "Completion Percentage",
          value: `${stats.ProjectCompletionPercentage.toFixed(2)}%`,
          iconColor: "text-emerald-600",
          gradientFrom: "from-emerald-500",
          gradientTo: "to-teal-600",
        },
      ]
    : [];

  if (error) {
    return (
      <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">Error loading KPI cards: {error}</p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => (
          <KPICard
            key={index}
            icon={card.icon}
            label={card.label}
            value={card.value}
            iconColor={card.iconColor}
            gradientFrom={card.gradientFrom}
            gradientTo={card.gradientTo}
            isLoading={isLoading}
          />
        ))}
        {isLoading && cards.length === 0 && (
          <>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 rounded-t-2xl"></div>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>
                    <div className="h-10 bg-gray-200 rounded w-1/2"></div>
                  </div>
                  <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default KPICards;
